/**
 * useSessionConsent - Hook for managing consent-based data sharing in advisor panel
 *
 * Provides:
 * - Fetching consent status and shared data
 * - Updating shared data with optimistic locking
 * - Real-time sync via WebSocket
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type {
  ConsentInfo,
  SharedData,
  SharedCredit,
  ScoringResult,
  CreditworthinessResult,
  PersonalDataSnapshot,
} from "../types";

// ============================================
// Types
// ============================================

interface ConsentResponse {
  consent: ConsentInfo;
  sharedData?: SharedData;
}

interface UpdateSharedDataParams {
  credits?: SharedCredit[];
  scoring?: ScoringResult;
  creditworthiness?: CreditworthinessResult;
  personalData?: PersonalDataSnapshot;
}

interface UseSessionConsentOptions {
  sessionToken: string;
  enabled?: boolean;
  onConsentGranted?: (scopes: ConsentInfo["sharedScopes"]) => void;
  onConsentRevoked?: () => void;
  onDataUpdated?: (data: SharedData) => void;
  onConflict?: (serverVersion: number, localVersion: number) => void;
}

interface UseSessionConsentReturn {
  // Consent status
  consent: ConsentInfo | undefined;
  isConsentGranted: boolean;

  // Shared data
  sharedData: SharedData | undefined;

  // Loading states
  isLoading: boolean;
  isUpdating: boolean;

  // Error states
  error: Error | null;
  updateError: Error | null;

  // Actions
  updateSharedData: (data: UpdateSharedDataParams) => Promise<void>;
  refetch: () => void;

  // Data version (for optimistic locking)
  version: number;
}

// ============================================
// Hook Implementation
// ============================================

export function useSessionConsent({
  sessionToken,
  enabled = true,
  onConsentGranted,
  onConsentRevoked,
  onDataUpdated,
  onConflict,
}: UseSessionConsentOptions): UseSessionConsentReturn {
  const queryClient = useQueryClient();
  const [updateError, setUpdateError] = useState<Error | null>(null);
  const previousConsentStatus = useRef<ConsentInfo["status"] | undefined>(undefined);

  // Query key for this session's consent
  const queryKey = ["session-consent", sessionToken];

  // Fetch consent and shared data
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.getSessionConsent(sessionToken);
      return response as ConsentResponse;
    },
    enabled: enabled && !!sessionToken,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000, // Consider data fresh for 10 seconds
  });

  // Handle consent status changes
  useEffect(() => {
    if (!data?.consent) return;

    const currentStatus = data.consent.status;
    const prevStatus = previousConsentStatus.current;

    if (prevStatus !== currentStatus) {
      if (currentStatus === "granted" && prevStatus !== "granted") {
        onConsentGranted?.(data.consent.sharedScopes);
      } else if (currentStatus === "revoked" && prevStatus === "granted") {
        onConsentRevoked?.();
      }
      previousConsentStatus.current = currentStatus;
    }
  }, [data?.consent, onConsentGranted, onConsentRevoked]);

  // Handle data updates
  useEffect(() => {
    if (data?.sharedData) {
      onDataUpdated?.(data.sharedData);
    }
  }, [data?.sharedData, onDataUpdated]);

  // Mutation for updating shared data
  const updateMutation = useMutation({
    mutationFn: async (params: UpdateSharedDataParams) => {
      const currentVersion = data?.sharedData?.version ?? 0;
      const response = await api.updateSharedData(sessionToken, params, currentVersion);
      return response.sharedData as SharedData;
    },
    onMutate: async (newData) => {
      setUpdateError(null);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ConsentResponse>(queryKey);

      // Optimistically update to the new value
      if (previousData) {
        queryClient.setQueryData<ConsentResponse>(queryKey, {
          ...previousData,
          sharedData: {
            ...previousData.sharedData,
            ...newData,
            lastEditedBy: "advisor",
            lastSyncedAt: new Date().toISOString(),
            version: (previousData.sharedData?.version ?? 0) + 1,
          },
        });
      }

      return { previousData };
    },
    onError: (err: Error & { status?: number }, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }

      // Handle version conflict
      if (err.status === 409) {
        const currentVersion = data?.sharedData?.version ?? 0;
        onConflict?.(currentVersion + 1, currentVersion); // Simplified - server would provide actual version
        refetch(); // Refresh to get latest data
      }

      setUpdateError(err);
    },
    onSuccess: (newData) => {
      // Update cache with server response
      const previousData = queryClient.getQueryData<ConsentResponse>(queryKey);
      if (previousData) {
        queryClient.setQueryData<ConsentResponse>(queryKey, {
          ...previousData,
          sharedData: newData,
        });
      }
    },
  });

  // Wrapped update function
  const updateSharedData = useCallback(
    async (params: UpdateSharedDataParams) => {
      await updateMutation.mutateAsync(params);
    },
    [updateMutation]
  );

  // Derived values
  const consent = data?.consent;
  const sharedData = data?.sharedData;
  const isConsentGranted = consent?.status === "granted";
  const version = sharedData?.version ?? 0;

  return {
    // Consent status
    consent,
    isConsentGranted,

    // Shared data
    sharedData,

    // Loading states
    isLoading,
    isUpdating: updateMutation.isPending,

    // Error states
    error: error as Error | null,
    updateError,

    // Actions
    updateSharedData,
    refetch,

    // Version
    version,
  };
}

// ============================================
// Helper hook for individual data sections
// ============================================

interface UseSharedCreditsOptions {
  sessionToken: string;
  enabled?: boolean;
}

export function useSharedCredits({ sessionToken, enabled = true }: UseSharedCreditsOptions) {
  const { sharedData, isConsentGranted, isLoading, updateSharedData, isUpdating } =
    useSessionConsent({ sessionToken, enabled });

  const credits = sharedData?.credits ?? [];
  const hasAccess = isConsentGranted && sharedData?.credits !== undefined;

  const updateCredits = useCallback(
    async (newCredits: SharedCredit[]) => {
      await updateSharedData({ credits: newCredits });
    },
    [updateSharedData]
  );

  const addCredit = useCallback(
    async (credit: Omit<SharedCredit, "id">) => {
      const newCredit: SharedCredit = {
        ...credit,
        id: `credit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
      await updateSharedData({ credits: [...credits, newCredit] });
    },
    [credits, updateSharedData]
  );

  const removeCredit = useCallback(
    async (creditId: string) => {
      await updateSharedData({ credits: credits.filter((c) => c.id !== creditId) });
    },
    [credits, updateSharedData]
  );

  const updateCredit = useCallback(
    async (creditId: string, updates: Partial<SharedCredit>) => {
      await updateSharedData({
        credits: credits.map((c) => (c.id === creditId ? { ...c, ...updates } : c)),
      });
    },
    [credits, updateSharedData]
  );

  return {
    credits,
    hasAccess,
    isLoading,
    isUpdating,
    updateCredits,
    addCredit,
    removeCredit,
    updateCredit,
  };
}

export function useSharedScoring({ sessionToken, enabled = true }: UseSharedCreditsOptions) {
  const { sharedData, isConsentGranted, isLoading, updateSharedData, isUpdating } =
    useSessionConsent({ sessionToken, enabled });

  const scoring = sharedData?.scoring;
  const hasAccess = isConsentGranted && sharedData?.scoring !== undefined;

  const updateScoring = useCallback(
    async (newScoring: ScoringResult) => {
      await updateSharedData({ scoring: newScoring });
    },
    [updateSharedData]
  );

  return {
    scoring,
    hasAccess,
    isLoading,
    isUpdating,
    updateScoring,
  };
}

export function useSharedCreditworthiness({ sessionToken, enabled = true }: UseSharedCreditsOptions) {
  const { sharedData, isConsentGranted, isLoading, updateSharedData, isUpdating } =
    useSessionConsent({ sessionToken, enabled });

  const creditworthiness = sharedData?.creditworthiness;
  const hasAccess = isConsentGranted && sharedData?.creditworthiness !== undefined;

  const updateCreditworthiness = useCallback(
    async (newCreditworthiness: CreditworthinessResult) => {
      await updateSharedData({ creditworthiness: newCreditworthiness });
    },
    [updateSharedData]
  );

  return {
    creditworthiness,
    hasAccess,
    isLoading,
    isUpdating,
    updateCreditworthiness,
  };
}

export function useSharedPersonalData({ sessionToken, enabled = true }: UseSharedCreditsOptions) {
  const { sharedData, isConsentGranted, isLoading, updateSharedData, isUpdating } =
    useSessionConsent({ sessionToken, enabled });

  const personalData = sharedData?.personalData;
  const hasAccess = isConsentGranted && sharedData?.personalData !== undefined;

  const updatePersonalData = useCallback(
    async (newPersonalData: PersonalDataSnapshot) => {
      await updateSharedData({ personalData: newPersonalData });
    },
    [updateSharedData]
  );

  return {
    personalData,
    hasAccess,
    isLoading,
    isUpdating,
    updatePersonalData,
  };
}
